import React, { useState } from 'react';
import axios from 'axios';
import '../styles/BroadcastMessenger.css';

function BroadcastMessenger() {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    link_text: '',
    link_action: '',
    start_date: '',
    expires_at: '',
    show_to_role: 'all',
    is_dismissible: true
  });

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.message) {
      alert('Please enter a title and message');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        link_text: formData.link_text || null,
        link_action: formData.link_action || null,
        start_date: formData.start_date || null,
        expires_at: formData.expires_at || null,
        show_to_role: formData.show_to_role,
        is_dismissible: formData.is_dismissible
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/broadcasts`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      
      setFormData({
        title: '',
        message: '',
        type: 'info',
        link_text: '',
        link_action: '',
        start_date: '',
        expires_at: '',
        show_to_role: 'all',
        is_dismissible: true
      });

      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error creating broadcast:', error);
      alert('Failed to create broadcast: ' + (error.response?.data?.error || error.message));
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      link_text: '',
      link_action: '',
      start_date: '',
      expires_at: '',
      show_to_role: 'all',
      is_dismissible: true
    });
    setSuccess(false);
  };

  const getIcon = () => {
    switch(formData.type) {
      case 'maintenance':
        return '🔧';
      case 'urgent':
        return '⚠️';
      case 'feature':
        return '✨';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className="broadcast-messenger">
      <div className="messenger-header">
        <h2>Broadcast Messenger</h2>
        <p>Create and schedule broadcast messages for all users</p>
      </div>

      {success && (
        <div className="success-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <span>Broadcast message created successfully!</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="messenger-form">
        <div className="form-card">
          <h3>Message Details</h3>
          
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Equipment Assignment Now Available!"
              required
            />
          </div>

          <div className="form-group">
            <label>Message *</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Complete your equipment audit by clicking the link below..."
              rows="4"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="info">Info (Blue)</option>
                <option value="feature">Feature (Green)</option>
                <option value="maintenance">Maintenance (Orange)</option>
                <option value="urgent">Urgent (Red)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Show To</label>
              <select
                name="show_to_role"
                value={formData.show_to_role}
                onChange={handleInputChange}
              >
                <option value="all">Everyone</option>
                <option value="staff">Staff Only</option>
                <option value="admin">Admins Only</option>
              </select>
            </div>
          </div>

          {/* PREVIEW SECTION - RIGHT HERE */}
          <div className="preview-container">
            <h4>Live Preview</h4>
            <div className={`broadcast-preview ${formData.type}`}>
              <div className="preview-content">
                <div className="preview-icon">{getIcon()}</div>
                <div className="preview-text">
                  <strong>{formData.title || 'Your Title Here'}</strong>
                  <span>{formData.message || 'Your message will appear here...'}</span>
                </div>
                {formData.link_text && (
                  <button className="preview-link" type="button">
                    {formData.link_text} →
                  </button>
                )}
              </div>
              {formData.is_dismissible && (
                <button className="preview-dismiss" type="button">✕</button>
              )}
            </div>
          </div>
        </div>

        <div className="form-card">
          <h3>Action Link (Optional)</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Link Text</label>
              <input
                type="text"
                name="link_text"
                value={formData.link_text}
                onChange={handleInputChange}
                placeholder="Get Started"
              />
            </div>

            <div className="form-group">
              <label>Link Action</label>
              <select
                name="link_action"
                value={formData.link_action}
                onChange={handleInputChange}
              >
                <option value="">No Action</option>
                <option value="equipment-assignment">Equipment Assignment</option>
                <option value="welcome-letter">Issue Welcome Letter</option>
                <option value="inventory">Office Inventory</option>
                <option value="notifications">Notifications</option>
                <option value="user-management">User Management</option>
                <option value="reports">Reports</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-card">
          <h3>Schedule</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Start Date (Optional)</label>
              <input
                type="datetime-local"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
              />
              <small>Leave blank to start immediately</small>
            </div>

            <div className="form-group">
              <label>End Date (Optional)</label>
              <input
                type="datetime-local"
                name="expires_at"
                value={formData.expires_at}
                onChange={handleInputChange}
              />
              <small>Leave blank to never expire</small>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_dismissible"
                checked={formData.is_dismissible}
                onChange={handleInputChange}
              />
              <span>Allow users to dismiss this message</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={resetForm} className="btn-cancel">
            Clear Form
          </button>
          <button type="submit" disabled={sending} className="btn-submit">
            {sending ? 'Creating...' : 'Create Broadcast'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BroadcastMessenger;